import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useLikes } from '../../hooks/useLikes';
import { supabase, type Post, type Job } from '../../lib/supabase';
import { mockFeedPosts } from '../../mocks/feedPosts';
import { mockJobPosts } from '../../mocks/jobPosts';
import { mockStudyPosts } from '../../mocks/studyPosts';
import { mockKnowledgePosts } from '../../mocks/knowledgePosts';
import ToastContainer, { showToast } from '../../components/base/Toast';
import { GAEvents } from '../../hooks/useGoogleAnalytics';

interface HackerNewsItem {
  id: number;
  title: string;
  url?: string;
  by: string;
  time: number;
  score: number;
  descendants?: number;
}

interface CommunityNews extends Post {
  type: 'news';
  url?: string;
}

// 로딩 스켈레톤 컴포넌트
function PostSkeleton() {
  return (
    <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 lg:p-6 animate-pulse">
      <div className="flex items-center space-x-3 mb-3">
        <div className="w-16 h-5 bg-[#333] rounded-full"></div>
        <div className="w-24 h-4 bg-[#333] rounded"></div>
      </div>
      <div className="w-3/4 h-6 bg-[#333] rounded mb-3"></div>
      <div className="w-full h-4 bg-[#333] rounded mb-2"></div>
      <div className="w-2/3 h-4 bg-[#333] rounded mb-4"></div>
      <div className="flex items-center space-x-4">
        <div className="w-12 h-4 bg-[#333] rounded"></div>
        <div className="w-12 h-4 bg-[#333] rounded"></div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const { user, profile, signOut } = useAuth();
  const { isLiked, toggleLike } = useLikes(user?.id);
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('feed');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showWriteModal, setShowWriteModal] = useState(false);
  const [writeModalType, setWriteModalType] = useState<'feed' | 'job' | 'study' | 'knowledge' | 'news'>('feed');
  const [submitting, setSubmitting] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Real data from Supabase with mock data fallback
  const [feedPosts, setFeedPosts] = useState<Post[]>(mockFeedPosts);
  const [jobPosts, setJobPosts] = useState<Job[]>(mockJobPosts);
  const [studyPosts, setStudyPosts] = useState<Post[]>(mockStudyPosts);
  const [knowledgePosts, setKnowledgePosts] = useState<Post[]>(mockKnowledgePosts);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState(false);

  // Hacker News
  const [hackerNews, setHackerNews] = useState<HackerNewsItem[]>([]);
  const [communityNews, setCommunityNews] = useState<CommunityNews[]>([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsCategory, setNewsCategory] = useState('all');

  // Job Search & Bookmark
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);
  const [bookmarkedJobs, setBookmarkedJobs] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 데이터 로딩 최적화 - Promise.all로 병렬 처리
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setDataError(false);

      // 모든 쿼리를 병렬로 실행 (jobs는 mock 데이터 사용)
      const [feedData, studyData, knowledgeData] = await Promise.all([
        supabase
          .from('posts')
          .select('*, profiles(*)')
          .eq('type', 'feed')
          .order('created_at', { ascending: false })
          .limit(20),
        supabase
          .from('posts')
          .select('*, profiles(*)')
          .eq('type', 'study')
          .order('created_at', { ascending: false })
          .limit(20),
        supabase
          .from('posts')
          .select('*, profiles(*)')
          .eq('type', 'knowledge')
          .order('created_at', { ascending: false })
          .limit(20),
      ]);

      // 데이터가 있으면 업데이트, 없으면 mock 데이터 유지
      if (feedData.data && feedData.data.length > 0) setFeedPosts(feedData.data);
      if (studyData.data && studyData.data.length > 0) setStudyPosts(studyData.data);
      if (knowledgeData.data && knowledgeData.data.length > 0) setKnowledgePosts(knowledgeData.data);
      // jobPosts는 항상 mockJobPosts 사용

    } catch (error) {
      console.error('Error loading data:', error);
      setDataError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    // Subscribe to real-time updates
    const postsChannel = supabase
      .channel('posts-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'posts' }, () => {
        loadData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(postsChannel);
    };
  }, [loadData]);

  const loadUnreadNotifications = useCallback(async () => {
    if (!user) return;

    try {
      const { count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      setUnreadCount(count || 0);
    } catch (error) {
      console.error('알림 개수 로드 실패:', error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadUnreadNotifications();

      // 알림 실시간 구독
      const notificationsChannel = supabase
        .channel('notifications-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`,
          },
          () => {
            loadUnreadNotifications();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(notificationsChannel);
      };
    }
  }, [user, loadUnreadNotifications]);

  // Load bookmarks from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('bookmarkedJobs');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setBookmarkedJobs(new Set(parsed));
      } catch (e) {
        console.error('Failed to load bookmarks:', e);
      }
    }
  }, []);

  // Save bookmarks to localStorage
  const toggleBookmark = (jobId: string) => {
    setBookmarkedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
        showToast('북마크가 해제되었습니다', 'info');
      } else {
        newSet.add(jobId);
        showToast('북마크에 추가되었습니다', 'success');
      }
      localStorage.setItem('bookmarkedJobs', JSON.stringify([...newSet]));
      return newSet;
    });
  };

  // Calculate D-day
  const calculateDday = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get D-day badge style
  const getDdayBadge = (dday: number) => {
    if (dday < 0) {
      return { label: '마감', cls: 'bg-gray-500/20 border-gray-500 text-gray-500' };
    } else if (dday <= 3) {
      return { label: `D-${dday}`, cls: 'bg-red-500/20 border-red-500 text-red-500' };
    } else if (dday <= 7) {
      return { label: `D-${dday}`, cls: 'bg-orange-500/20 border-orange-500 text-orange-500' };
    } else {
      return { label: `D-${dday}`, cls: 'bg-green-500/20 border-green-500 text-green-500' };
    }
  };

  // Hacker News 로딩 최적화 - 타임아웃 및 에러 처리
  const loadHackerNews = useCallback(async () => {
    try {
      setNewsLoading(true);
      
      // 타임아웃 설정 (5초)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('https://hacker-news.firebaseio.com/v0/topstories.json', {
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      const storyIds = await response.json();
      
      // 상위 10개 스토리만 가져오기
      const storyPromises = storyIds.slice(0, 10).map(async (id: number) => {
        try {
          const storyResponse = await fetch(`https://hacker-news.firebaseio.com/v0/item/${id}.json`);
          return storyResponse.json();
        } catch (error) {
          console.error(`Failed to load story ${id}:`, error);
          return null;
        }
      });

      const stories = await Promise.all(storyPromises);
      setHackerNews(stories.filter(story => story && story.title));
    } catch (error) {
      console.error('Hacker News 로드 실패:', error);
      setHackerNews([]); // 에러 시 빈 배열
    } finally {
      setNewsLoading(false);
    }
  }, []);

  const loadCommunityNews = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('posts')
        .select('*, profiles(*)')
        .eq('type', 'news')
        .order('created_at', { ascending: false })
        .limit(20);

      if (data) {
        setCommunityNews(data as CommunityNews[]);
      }
    } catch (error) {
      console.error('커뮤니티 뉴스 로드 실패:', error);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'news') {
      loadHackerNews();
      loadCommunityNews();
    }
  }, [activeTab, loadHackerNews, loadCommunityNews]);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setShowLangDropdown(false);
  };

  const openWriteModal = (type: 'feed' | 'job' | 'study' | 'knowledge' | 'news') => {
    if (!user) {
      showToast('로그인이 필요합니다', 'info');
      navigate('/auth');
      return;
    }
    setWriteModalType(type);
    setShowWriteModal(true);
  };

  const handleSubmitPost = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!user) {
      showToast('로그인이 필요합니다', 'error');
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      const title = formData.get('title') as string;
      const content = formData.get('content') as string;
      const tagsString = formData.get('tags') as string;
      const tags = tagsString ? tagsString.split(',').map(t => t.trim()).filter(Boolean) : [];

      if (!title?.trim() || !content?.trim()) {
        showToast('제목과 내용을 입력해주세요', 'error');
        setSubmitting(false);
        return;
      }

      if (writeModalType === 'job') {
        // Job post with enhanced fields
        const company = formData.get('company') as string;
        const position = formData.get('position') as string;
        const category = formData.get('category') as string;
        const deadline = formData.get('deadline') as string;
        const region = formData.get('region') as string;
        const level = formData.get('level') as string;
        const salary = formData.get('salary') as string;
        const location = formData.get('location') as string;
        const experience = formData.get('experience') as string;
        const description = formData.get('description') as string;
        const apply_url = formData.get('apply_url') as string;
        
        // Parse multi-line text areas into arrays
        const responsibilitiesText = formData.get('responsibilities') as string;
        const requirementsText = formData.get('requirements') as string;
        const preferredText = formData.get('preferred') as string;
        
        const responsibilities = responsibilitiesText 
          ? responsibilitiesText.split('\n').map(t => t.trim()).filter(Boolean) 
          : [];
        const requirements = requirementsText 
          ? requirementsText.split('\n').map(t => t.trim()).filter(Boolean) 
          : [];
        const preferred = preferredText 
          ? preferredText.split('\n').map(t => t.trim()).filter(Boolean) 
          : [];

        // Validation
        if (!company?.trim() || !position?.trim() || !category || !deadline || !description?.trim()) {
          showToast('필수 항목을 모두 입력해주세요', 'error');
          setSubmitting(false);
          return;
        }

        const { data: jobData, error } = await supabase.from('jobs').insert({
          user_id: user.id,
          company,
          position,
          category,
          deadline,
          region,
          level,
          salary,
          location,
          experience,
          content: description,
          apply_url: apply_url || null,
          responsibilities,
          requirements,
          preferred,
          tags,
          status: 'active'
        }).select().single();

        if (error) throw error;

        GAEvents.createPost('job');
        showToast('채용공고가 등록되었습니다', 'success');
        setShowWriteModal(false);
        loadData();
        
        // Navigate to job detail page
        if (jobData) {
          navigate(`/job/${jobData.id}`);
        }
      } else if (writeModalType === 'knowledge') {
        // Knowledge bounty post
        const bounty = parseInt(formData.get('bounty') as string) || 0;

        const { error } = await supabase.from('posts').insert({
          user_id: user.id,
          type: 'knowledge',
          title,
          content,
          tags,
          bounty_points: bounty,
          status: 'pending'
        });

        if (error) throw error;

        GAEvents.createPost('knowledge');
        showToast('현상금 질문이 등록되었습니다', 'success');
        setShowWriteModal(false);
        loadData();
      } else if (writeModalType === 'news') {
        // News post
        const url = formData.get('url') as string;

        const { error } = await supabase.from('posts').insert({
          user_id: user.id,
          type: 'news',
          title,
          content,
          tags,
          url: url || null
        });

        if (error) throw error;

        GAEvents.createPost('news');
        showToast('뉴스가 공유되었습니다', 'success');
        setShowWriteModal(false);
        loadData();
      } else {
        // Feed, Study posts
        const category = writeModalType === 'feed' 
          ? (formData.get('category') as string) 
          : writeModalType;

        const { error } = await supabase.from('posts').insert({
          user_id: user.id,
          type: writeModalType,
          title,
          content,
          category,
          tags
        });

        if (error) throw error;

        GAEvents.createPost(writeModalType);
        showToast('게시글이 작성되었습니다', 'success');
        setShowWriteModal(false);
        loadData();
      }

      setShowWriteModal(false);
      loadData();
    } catch (error) {
      console.error('Error creating post:', error);
      showToast('게시글 작성에 실패했습니다', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (postId: string) => {
    if (!user) {
      showToast('로그인이 필요합니다', 'error');
      return;
    }

    try {
      const result = await toggleLike(postId);
      
      if (result?.liked) {
        GAEvents.likePost(postId);
      }

      loadData();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

    if (diffInMinutes < 60) {
      return `${diffInMinutes}`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d`;
    }
  };

  const learningSites = [
    { name: 'HackTheBox', category: 'Practice', difficulty: 'All Levels', rating: 4.9, users: '2M+', url: 'hackthebox.com' },
    { name: 'TryHackMe', category: 'Learning', difficulty: 'Beginner', rating: 4.8, users: '1.5M+', url: 'tryhackme.com' },
    { name: 'PortSwigger Academy', category: 'Web Security', difficulty: 'Intermediate', rating: 4.9, users: '500K+', url: 'portswigger.net' },
    { name: 'PentesterLab', category: 'Pentesting', difficulty: 'Advanced', rating: 4.7, users: '300K+', url: 'pentesterlab.com' },
    { name: 'OverTheWire', category: 'Wargames', difficulty: 'All Levels', rating: 4.8, users: '800K+', url: 'overthewire.org' },
    { name: 'VulnHub', category: 'VM Practice', difficulty: 'Advanced', rating: 4.6, users: '400K+', url: 'vulnhub.com' },
  ];

  const premiumArchives = [
    { title: 'Fortune 500 기업 침투 테스트 실전 케이스', cases: 47, difficulty: 'Expert', locked: true },
    { title: '제로데이 발견부터 패치까지 전 과정', cases: 23, difficulty: 'Advanced', locked: true },
    { title: 'APT 공격 분석 및 대응 시나리오', cases: 31, difficulty: 'Expert', locked: true },
    { title: '랜섬웨어 리버스 엔지니어링 과정', cases: 18, difficulty: 'Advanced', locked: true },
  ];

  const menuItems = [
    { id: 'feed', icon: 'ri-home-5-line', label: t('menu.feed') },
    { id: 'jobs', icon: 'ri-briefcase-line', label: t('menu.jobs') },
    { id: 'study', icon: 'ri-book-open-line', label: t('menu.study') },
    { id: 'knowledge', icon: 'ri-question-answer-line', label: t('menu.knowledge') },
    { id: 'news', icon: 'ri-newspaper-line', label: t('menu.news') },
    { id: 'bugbounty', icon: 'ri-bug-line', label: t('menu.bugbounty') },
    { id: 'learning', icon: 'ri-links-line', label: t('menu.learning') },
    { id: 'premium', icon: 'ri-vip-crown-line', label: t('menu.premium') },
  ];

  const [jobCategoryFilter, setJobCategoryFilter] = useState('all');
  const [jobRegionFilter, setJobRegionFilter] = useState('all');
  const [jobLevelFilter, setJobLevelFilter] = useState('all');

  const filteredJobPosts = (() => {
    let filtered = jobPosts.filter((job) => {
      const j = job as any;
      const categoryMatch = jobCategoryFilter === 'all' || j.category === jobCategoryFilter;
      const regionMatch = jobRegionFilter === 'all' || j.region === jobRegionFilter;
      const levelMatch = jobLevelFilter === 'all' || j.level === jobLevelFilter;
      
      // Search filter
      const searchMatch = !jobSearchQuery || 
        job.company.toLowerCase().includes(jobSearchQuery.toLowerCase()) ||
        job.position.toLowerCase().includes(jobSearchQuery.toLowerCase()) ||
        job.tags?.some(tag => tag.toLowerCase().includes(jobSearchQuery.toLowerCase()));
      
      // Bookmark filter
      const bookmarkMatch = !showBookmarkedOnly || bookmarkedJobs.has(job.id);
      
      return categoryMatch && regionMatch && levelMatch && searchMatch && bookmarkMatch;
    });

    return filtered;
  })();

  const filteredFeedPosts = (() => {
    const filtered = selectedCategory === 'all'
      ? feedPosts
      : feedPosts.filter((post) => post.category === selectedCategory);

    return [...filtered].sort((a, b) => {
      const aPinned = (a as any).is_pinned ? 1 : 0;
      const bPinned = (b as any).is_pinned ? 1 : 0;
      return bPinned - aPinned;
    });
  })();

  const filteredNews = () => {
    const allNews = [
      ...hackerNews.map(n => ({ ...n, source: 'hackernews' as const })),
      ...communityNews.map(n => ({ ...n, source: 'community' as const }))
    ];

    if (newsCategory === 'all') return allNews;

    const categoryKeywords: Record<string, string[]> = {
      vulnerability: ['vulnerability', 'CVE', 'zero-day', 'exploit', 'bug'],
      breach: ['breach', 'leak', 'data', 'stolen', 'exposed'],
      malware: ['malware', 'ransomware', 'virus', 'trojan', 'worm'],
      bounty: ['bug bounty', 'bounty', 'reward', 'disclosure']
    };

    const keywords = categoryKeywords[newsCategory] || [];
    return allNews.filter(news => {
      const title = 'title' in news ? news.title.toLowerCase() : '';
      return keywords.some(keyword => title.includes(keyword));
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-['Space_Grotesk'] pb-20 lg:pb-0">
      <ToastContainer />
      {/* Top Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-[#00ff41]/20' : 'bg-[#0a0a0a] border-b border-[#00ff41]/20'}`}>
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-3 lg:py-4 flex items-center justify-between">
          <button
            onClick={() => window.location.href = '/'}
            className="flex items-center space-x-2 font-['Space_Mono'] cursor-pointer"
          >
            <span className="text-[#00ff41] text-base lg:text-xl font-bold">&gt; HACKER_LOUNGE</span>
            <span className="w-1.5 h-4 lg:w-2 lg:h-5 bg-[#00ff41] animate-pulse"></span>
          </button>

          <div className="flex items-center space-x-2 lg:space-x-6">
            {/* Desktop Search */}
            <div className="relative hidden lg:block">
              <input
                type="text"
                placeholder={t('nav.search.placeholder')}
                className="bg-[#1a1a1a] border border-[#333] rounded-full px-6 py-2 pr-12 w-96 text-sm text-white placeholder-[#666] focus:border-[#00ff41] focus:outline-none transition-colors cursor-pointer"
                onFocus={() => navigate('/search')}
                readOnly
              />
              <i className="ri-search-line absolute right-4 top-1/2 -translate-y-1/2 text-[#666]"></i>
            </div>

            {/* Mobile Search Icon */}
            <button
              onClick={() => navigate('/search')}
              className="w-9 h-9 lg:hidden flex items-center justify-center border border-[#333] rounded-full hover:border-[#00ff41] transition-colors cursor-pointer"
            >
              <i className="ri-search-line text-[#666] hover:text-[#00ff41]"></i>
            </button>

            <button
              onClick={() => navigate('/notifications')}
              className="relative w-9 h-9 lg:w-10 lg:h-10 flex items-center justify-center border border-[#333] rounded-full hover:border-[#00ff41] transition-colors cursor-pointer"
            >
              <i className="ri-notification-3-line text-[#666] hover:text-[#00ff41]"></i>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setShowLangDropdown(!showLangDropdown)}
                className="flex items-center space-x-1 lg:space-x-2 border border-[#333] rounded-full px-2 lg:px-4 py-1.5 lg:py-2 hover:border-[#00ff41] transition-colors cursor-pointer"
              >
                <span className="text-lg lg:text-xl">{currentLanguage.flag}</span>
                <span className="text-xs lg:text-sm text-[#666] hidden lg:inline">{currentLanguage.code.toUpperCase()}</span>
                <i className={`ri-arrow-down-s-line text-[#666] transition-transform text-sm ${showLangDropdown ? 'rotate-180' : ''}`}></i>
              </button>

              {showLangDropdown && (
                <div className="absolute top-full right-0 mt-2 bg-[#1a1a1a] border border-[#333] rounded-lg overflow-hidden min-w-[160px]">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 hover:bg-[#00ff41]/10 transition-colors cursor-pointer ${
                        i18n.language === lang.code ? 'bg-[#00ff41] text-black' : 'text-white'
                      }`}
                    >
                      <span className="text-xl">{lang.flag}</span>
                      <span className="text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {user ? (
              <button
                onClick={() => navigate('/profile')}
                className="hidden lg:flex items-center space-x-2 border-2 border-[#00ff41] text-[#00ff41] px-4 py-2 rounded-full hover:bg-[#00ff41] hover:text-black transition-all duration-300 font-['Space_Mono'] text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-line"></i>
                <span>{profile?.username || 'Profile'}</span>
              </button>
            ) : (
              <button
                onClick={() => navigate('/auth')}
                className="hidden lg:block border-2 border-[#00ff41] text-[#00ff41] px-6 py-2 rounded-full hover:bg-[#00ff41] hover:text-black transition-all duration-300 font-['Space_Mono'] text-sm whitespace-nowrap cursor-pointer"
              >
                로그인
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-16 lg:pt-20 max-w-[1600px] mx-auto px-4 lg:px-6 py-4 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8">
          {/* Desktop Left Sidebar */}
          <div className="hidden lg:block lg:col-span-2">
            <div className="sticky top-24 space-y-2">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg font-['Space_Mono'] text-sm transition-all cursor-pointer whitespace-nowrap flex items-center space-x-3 ${
                    activeTab === item.id ? 'bg-[#00ff41] text-black' : 'text-[#666] hover:text-[#00ff41] hover:bg-[#1a1a1a]'
                  }`}
                >
                  <i className={item.icon}></i>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-7">
            {/* Feed Tab */}
            {activeTab === 'feed' && (
              <div>
                <div className="flex items-center justify-between mb-4 lg:mb-6 mt-4 lg:mt-0">
                  <h1 className="text-2xl lg:text-3xl font-black">{t('feed.title')}</h1>
                  <button
                    onClick={() => openWriteModal('feed')}
                    className="bg-[#00ff41] text-black px-4 lg:px-6 py-2 rounded-full font-['Space_Mono'] text-xs lg:text-sm font-bold hover:shadow-[0_0_20px_rgba(0,255,65,0.5)] transition-all cursor-pointer whitespace-nowrap"
                  >
                    {t('feed.new.post')}
                  </button>
                </div>

                <div className="flex items-center space-x-2 lg:space-x-3 mb-4 lg:mb-6 overflow-x-auto pb-2 scrollbar-hide">
                  {['all', 'vulnerability', 'malware', 'tools', 'ctf', 'career', 'bugbounty', 'redteam'].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-3 lg:px-4 py-1.5 lg:py-2 rounded-full font-['Space_Mono'] text-xs transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                        selectedCategory === cat
                          ? 'bg-[#00ff41] text-black'
                          : 'bg-[#1a1a1a] text-[#666] hover:text-[#00ff41] border border-[#333]'
                      }`}
                    >
                      {t(`feed.category.${cat}`)}
                    </button>
                  ))}
                </div>

                {loading ? (
                  <div className="space-y-3 lg:space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <PostSkeleton key={i} />
                    ))}
                  </div>
                ) : dataError ? (
                  <div className="text-center py-12">
                    <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
                    <p className="text-[#666] mb-4">데이터를 불러오는데 실패했습니다</p>
                    <button
                      onClick={loadData}
                      className="bg-[#00ff41] text-black px-6 py-2 rounded-full font-['Space_Mono'] text-sm font-bold hover:shadow-[0_0_20px_rgba(0,255,65,0.5)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      다시 시도
                    </button>
                  </div>
                ) : filteredFeedPosts.length === 0 ? (
                  <div className="text-center py-12 text-[#666]">게시글이 없습니다</div>
                ) : (
                  <div className="space-y-3 lg:space-y-4">
                    {filteredFeedPosts.map((post) => {
                      const isPinned = (post as any).is_pinned;
                      const getCategoryColor = (category: string) => {
                        const colors: Record<string, string> = {
                          vulnerability: 'bg-red-500/20 border-red-500 text-red-500',
                          malware: 'bg-orange-500/20 border-orange-500 text-orange-500',
                          tools: 'bg-blue-500/20 border-blue-500 text-blue-500',
                          ctf: 'bg-purple-500/20 border-purple-500 text-purple-500',
                          career: 'bg-yellow-500/20 border-yellow-500 text-yellow-500',
                          bugbounty: 'bg-green-500/20 border-green-500 text-green-500',
                          redteam: 'bg-red-600/20 border-red-600 text-red-600',
                        };
                        return colors[category] || 'bg-[#00ff41]/20 border-[#00ff41] text-[#00ff41]';
                      };
                      return (
                      <div
                        key={post.id}
                        className={`border rounded-lg p-4 lg:p-6 hover:border-[#00ff41] transition-all cursor-pointer relative ${
                          isPinned
                            ? 'bg-[#0f1a0f] border-[#00ff41]/50 shadow-[0_0_12px_rgba(0,255,65,0.08)]'
                            : 'bg-[#1a1a1a] border-[#333]'
                        }`}
                        onClick={() => navigate(`/post/${post.id}`)}
                      >
                        {isPinned && (
                          <div className="absolute top-3 right-3 flex items-center space-x-1 bg-[#00ff41]/10 border border-[#00ff41]/40 rounded-full px-2 py-0.5">
                            <i className="ri-pushpin-fill text-[#00ff41] text-xs"></i>
                            <span className="text-[#00ff41] text-[10px] font-['Space_Mono'] font-bold">PINNED</span>
                          </div>
                        )}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 pr-16">
                            <div className="flex items-center space-x-2 lg:space-x-3 mb-2 flex-wrap gap-2">
                              {post.category && (
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getCategoryColor(post.category)}`}>
                                  {post.category.toUpperCase()}
                                </span>
                              )}
                              <span className="font-['Space_Mono'] text-[#00ff41] text-xs">@{post.profiles?.username}</span>
                              <span className="text-[#666] text-xs">•</span>
                              <span className="text-[#666] text-xs">{getTimeAgo(post.created_at)} {t('time.minutes.ago')}</span>
                            </div>
                            <h3 className="text-white text-base lg:text-lg font-semibold mb-2 lg:mb-3">{post.title}</h3>
                            <p className="text-[#888] text-sm mb-3 line-clamp-2">{post.content}</p>
                            <div className="flex items-center space-x-2 flex-wrap gap-2">
                              {post.tags?.map((tag, idx) => (
                                <span key={idx} className="bg-[#0a0a0a] border border-[#00ff41]/30 text-[#00ff41] px-2 lg:px-3 py-1 rounded-full text-xs">
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-4 lg:space-x-6 text-sm text-[#666]">
                          <div className="flex items-center space-x-2 hover:text-[#00ff41] transition-colors cursor-pointer">
                            <i className="ri-chat-3-line"></i>
                            <span>{post.replies_count}</span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLike(post.id);
                            }}
                            className={`flex items-center space-x-2 transition-colors cursor-pointer ${
                              isLiked(post.id) ? 'text-[#00ff41]' : 'hover:text-[#00ff41]'
                            }`}
                          >
                            <i className={isLiked(post.id) ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                            <span>{post.likes_count}</span>
                          </button>
                          <div className="flex items-center space-x-2 hover:text-[#00ff41] transition-colors cursor-pointer">
                            <i className="ri-share-line"></i>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Jobs Tab */}
            {activeTab === 'jobs' && (
              <div>
                <div className="flex items-center justify-between mb-4 lg:mb-6 mt-4 lg:mt-0">
                  <h1 className="text-2xl lg:text-3xl font-black">{t('jobs.title')}</h1>
                  <button
                    onClick={() => openWriteModal('job')}
                    className="bg-[#00ff41] text-black px-4 lg:px-6 py-2 rounded-full font-['Space_Mono'] text-xs lg:text-sm font-bold hover:shadow-[0_0_20px_rgba(0,255,65,0.5)] transition-all cursor-pointer whitespace-nowrap"
                  >
                    {t('jobs.post.job')}
                  </button>
                </div>

                {/* Search & Bookmark Bar */}
                <div className="bg-[#111] border border-[#222] rounded-xl p-4 mb-5 space-y-3">
                  {/* Search Input */}
                  <div className="relative">
                    <input
                      type="text"
                      value={jobSearchQuery}
                      onChange={(e) => setJobSearchQuery(e.target.value)}
                      placeholder="회사명, 포지션, 태그로 검색..."
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-[#666] focus:border-[#00ff41] focus:outline-none transition-colors"
                    />
                    <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-[#666]"></i>
                    {jobSearchQuery && (
                      <button
                        onClick={() => setJobSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-[#666] hover:text-white transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    )}
                  </div>

                  {/* Bookmark Toggle */}
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setShowBookmarkedOnly(!showBookmarkedOnly)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-['Space_Mono'] transition-all cursor-pointer whitespace-nowrap ${
                        showBookmarkedOnly
                          ? 'bg-[#00ff41] text-black font-bold'
                          : 'bg-[#1a1a1a] text-[#888] border border-[#333] hover:border-[#00ff41] hover:text-[#00ff41]'
                      }`}
                    >
                      <i className={showBookmarkedOnly ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
                      북마크만 보기
                      {bookmarkedJobs.size > 0 && (
                        <span className="ml-1">({bookmarkedJobs.size})</span>
                      )}
                    </button>
                    {(jobSearchQuery || showBookmarkedOnly) && (
                      <span className="text-[#00ff41] text-sm font-['Space_Mono']">
                        {filteredJobPosts.length}개 결과
                      </span>
                    )}
                  </div>
                </div>

                {loading ? (
                  <div className="space-y-3 lg:space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <PostSkeleton key={i} />
                    ))}
                  </div>
                ) : filteredJobPosts.length === 0 ? (
                  <div className="text-center py-16 text-[#666]">
                    <i className="ri-briefcase-line text-4xl mb-3 block text-[#333]"></i>
                    <p className="mb-2">
                      {showBookmarkedOnly 
                        ? '북마크한 채용공고가 없습니다' 
                        : jobSearchQuery 
                        ? '검색 결과가 없습니다' 
                        : '조건에 맞는 채용공고가 없습니다'}
                    </p>
                    <button
                      onClick={() => {
                        setJobCategoryFilter('all');
                        setJobRegionFilter('all');
                        setJobLevelFilter('all');
                        setJobSearchQuery('');
                        setShowBookmarkedOnly(false);
                      }}
                      className="text-[#00ff41] text-sm hover:underline cursor-pointer"
                    >
                      전체 보기
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3 lg:space-y-4">
                    {filteredJobPosts.map((job) => {
                      const j = job as any;
                      const dday = calculateDday(job.deadline);
                      const ddayBadge = getDdayBadge(dday);
                      const regionBadge = j.region === 'domestic'
                        ? { label: '국내', cls: 'bg-emerald-500/15 border-emerald-500/50 text-emerald-400' }
                        : { label: '해외', cls: 'bg-sky-500/15 border-sky-500/50 text-sky-400' };
                      const levelBadge = j.level === 'junior'
                        ? { label: '신입', cls: 'bg-yellow-500/15 border-yellow-500/50 text-yellow-400' }
                        : { label: '경력', cls: 'bg-orange-500/15 border-orange-500/50 text-orange-400' };
                      const isBookmarked = bookmarkedJobs.has(job.id);
                      
                      return (
                        <div
                          key={job.id}
                          className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 lg:p-6 hover:border-[#00ff41] transition-all cursor-pointer relative"
                          onClick={() => navigate(`/job/${job.id}`)}
                        >
                          {/* Bookmark Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleBookmark(job.id);
                            }}
                            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-[#0a0a0a] border border-[#333] hover:border-[#00ff41] transition-colors z-10 cursor-pointer"
                          >
                            <i className={`${isBookmarked ? 'ri-bookmark-fill text-[#00ff41]' : 'ri-bookmark-line text-[#666]'}`}></i>
                          </button>

                          <div className="flex items-start justify-between mb-3 pr-12">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                {/* D-day Badge */}
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${ddayBadge.cls}`}>
                                  {ddayBadge.label}
                                </span>
                                {j.region && (
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${regionBadge.cls}`}>
                                    {regionBadge.label}
                                  </span>
                                )}
                                {j.level && (
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${levelBadge.cls}`}>
                                    {levelBadge.label}
                                  </span>
                                )}
                              </div>
                              <h3 className="text-white text-lg lg:text-xl font-bold mb-1">{job.position}</h3>
                              <p className="text-[#00ff41] font-['Space_Mono'] text-sm mb-3">{job.company}</p>
                            </div>
                            <div className="text-right ml-4 flex-shrink-0">
                              <div className="text-[#00ff41] text-base lg:text-xl font-bold mb-1 leading-tight">{job.salary}</div>
                              <div className="text-[#666] text-xs">{t('jobs.salary')}</div>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3 lg:gap-4 mb-4">
                            <div>
                              <div className="text-[#666] text-xs mb-1">{t('jobs.location')}</div>
                              <div className="text-white text-sm">{job.location}</div>
                            </div>
                            <div>
                              <div className="text-[#666] text-xs mb-1">{t('jobs.experience')}</div>
                              <div className="text-white text-sm">{job.experience}</div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 mb-4 flex-wrap gap-2">
                            {job.tags?.map((tag, tidx) => (
                              <span key={tidx} className="bg-[#0a0a0a] border border-[#00ff41]/30 text-[#00ff41] px-2 lg:px-3 py-1 rounded-full text-xs">
                                #{tag}
                              </span>
                            ))}
                          </div>

                          <button className="w-full bg-[#00ff41] text-black py-3 rounded-lg font-['Space_Mono'] font-bold hover:shadow-[0_0_20px_rgba(0,255,65,0.5)] transition-all cursor-pointer whitespace-nowrap">
                            {t('jobs.apply.now')}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Study Tab */}
            {activeTab === 'study' && (
              <div>
                <div className="flex items-center justify-between mb-4 lg:mb-6 mt-4 lg:mt-0">
                  <h1 className="text-2xl lg:text-3xl font-black">{t('study.title')}</h1>
                  <button
                    onClick={() => openWriteModal('study')}
                    className="bg-[#00ff41] text-black px-4 lg:px-6 py-2 rounded-full font-['Space_Mono'] text-xs lg:text-sm font-bold hover:shadow-[0_0_20px_rgba(0,255,65,0.5)] transition-all cursor-pointer whitespace-nowrap"
                  >
                    {t('study.ask.question')}
                  </button>
                </div>

                {loading ? (
                  <div className="space-y-3 lg:space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <PostSkeleton key={i} />
                    ))}
                  </div>
                ) : studyPosts.length === 0 ? (
                  <div className="text-center py-12 text-[#666]">질문이 없습니다</div>
                ) : (
                  <div className="space-y-3 lg:space-y-4">
                    {studyPosts.map((q) => (
                      <div
                        key={q.id}
                        className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 lg:p-6 hover:border-[#00ff41] transition-all cursor-pointer"
                        onClick={() => navigate(`/post/${q.id}`)}
                      >
                        <h3 className="text-white text-base lg:text-lg font-semibold mb-2">{q.title}</h3>
                        <p className="text-[#888] text-sm line-clamp-2">{q.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Knowledge Tab */}
            {activeTab === 'knowledge' && (
              <div>
                <div className="flex items-center justify-between mb-4 lg:mb-6 mt-4 lg:mt-0">
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-black mb-2">{t('knowledge.title')}</h1>
                    <p className="text-[#666] text-sm">{t('knowledge.subtitle')}</p>
                  </div>
                  <button
                    onClick={() => openWriteModal('knowledge')}
                    className="bg-[#00ff41] text-black px-3 lg:px-6 py-2 rounded-full font-['Space_Mono'] text-xs lg:text-sm font-bold hover:shadow-[0_0_20px_rgba(0,255,65,0.5)] transition-all cursor-pointer whitespace-nowrap"
                  >
                    {t('knowledge.ask.bounty')}
                  </button>
                </div>

                {loading ? (
                  <div className="space-y-3 lg:space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <PostSkeleton key={i} />
                    ))}
                  </div>
                ) : knowledgePosts.length === 0 ? (
                  <div className="text-center py-12 text-[#666]">질문이 없습니다</div>
                ) : (
                  <div className="space-y-3 lg:space-y-4">
                    {knowledgePosts.map((kq) => (
                      <div
                        key={kq.id}
                        className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 lg:p-6 hover:border-[#00ff41] transition-all cursor-pointer"
                        onClick={() => navigate(`/post/${kq.id}`)}
                      >
                        <div className="flex flex-col lg:flex-row items-start justify-between mb-4 gap-4">
                          <div className="flex-1 w-full">
                            <div className="flex items-center space-x-2 lg:space-x-3 mb-3">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                kq.status === 'answered'
                                  ? 'bg-[#00ff41] text-black'
                                  : 'bg-[#1a1a1a] border border-[#00ff41] text-[#00ff41]'
                              }`}>
                                {kq.status === 'answered' ? t('knowledge.answered') : t('knowledge.pending')}
                              </span>
                              <span className="text-[#666] text-xs">{getTimeAgo(kq.created_at)} {t('time.minutes.ago')}</span>
                            </div>
                            <h3 className="text-white text-base lg:text-lg font-semibold mb-3 break-words">{kq.title}</h3>
                            <div className="flex items-center space-x-4 text-sm text-[#666]">
                              <div className="flex items-center space-x-2">
                                <i className="ri-chat-3-line"></i>
                                <span>{kq.replies_count} {t('study.answers')}</span>
                              </div>
                            </div>
                          </div>

                          <div className="w-full lg:w-auto lg:ml-6">
                            <div className="bg-[#00ff41]/10 border border-[#00ff41] rounded-lg px-4 py-3 text-center">
                              <div className="text-[#00ff41] text-2xl font-bold">{kq.bounty_points}</div>
                              <div className="text-[#00ff41] text-xs font-['Space_Mono']">{t('knowledge.points')}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* News Tab */}
            {activeTab === 'news' && (
              <div>
                <div className="flex items-center justify-between mb-4 lg:mb-6 mt-4 lg:mt-0">
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-black">{t('news.title')}</h1>
                    <p className="text-[#666] text-sm mt-1">{t('news.subtitle')}</p>
                  </div>
                  <button
                    onClick={() => openWriteModal('news')}
                    className="bg-[#00ff41] text-black px-4 lg:px-6 py-2 rounded-full font-['Space_Mono'] text-xs lg:text-sm font-bold hover:shadow-[0_0_20px_rgba(0,255,65,0.5)] transition-all cursor-pointer whitespace-nowrap"
                  >
                    {t('news.share.news')}
                  </button>
                </div>

                <div className="flex items-center space-x-2 lg:space-x-3 mb-4 lg:mb-6 overflow-x-auto pb-2 scrollbar-hide">
                  {['all', 'vulnerability', 'breach', 'malware', 'bounty'].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setNewsCategory(cat)}
                      className={`px-3 lg:px-4 py-1.5 lg:py-2 rounded-full font-['Space_Mono'] text-xs transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                        newsCategory === cat
                          ? 'bg-[#00ff41] text-black'
                          : 'bg-[#1a1a1a] text-[#666] hover:text-[#00ff41] border border-[#333]'
                      }`}
                    >
                      {t(`news.category.${cat}`)}
                    </button>
                  ))}
                </div>

                {newsLoading ? (
                  <div className="space-y-3 lg:space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <PostSkeleton key={i} />
                    ))}
                  </div>
                ) : filteredNews().length === 0 ? (
                  <div className="text-center py-12 text-[#666]">뉴스가 없습니다</div>
                ) : (
                  <div className="space-y-3 lg:space-y-4">
                    {filteredNews().map((news, idx) => {
                      const isHackerNews = 'by' in news;
                      return (
                        <div
                          key={isHackerNews ? `hn-${news.id}` : `cn-${news.id}`}
                          className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 lg:p-6 hover:border-[#00ff41] transition-all cursor-pointer"
                          onClick={() => {
                            if (isHackerNews && news.url) {
                              window.open(news.url, '_blank');
                            } else if (!isHackerNews) {
                              if (news.url) {
                                window.open(news.url, '_blank');
                              } else {
                                navigate(`/post/${news.id}`);
                              }
                            }
                          }}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 lg:space-x-3 mb-2">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  isHackerNews 
                                    ? 'bg-[#ff6600]/20 border border-[#ff6600] text-[#ff6600]'
                                    : 'bg-[#00ff41]/20 border border-[#00ff41] text-[#00ff41]'
                                }`}>
                                  {isHackerNews ? 'Hacker News' : t('news.community')}
                                </span>
                                <span className="font-['Space_Mono'] text-[#666] text-xs">
                                  {isHackerNews ? `@${news.by}` : `@${news.profiles?.username}`}
                                </span>
                                <span className="text-[#666] text-xs">•</span>
                                <span className="text-[#666] text-xs">
                                  {isHackerNews 
                                    ? getTimeAgo(new Date(news.time * 1000).toISOString())
                                    : getTimeAgo(news.created_at)
                                  } {t('time.minutes.ago')}
                                </span>
                              </div>
                              <h3 className="text-white text-base lg:text-lg font-semibold mb-2 lg:mb-3 leading-snug">
                                {news.title}
                              </h3>
                              {!isHackerNews && news.content && (
                                <p className="text-[#888] text-sm mb-3 line-clamp-2">{news.content}</p>
                              )}
                              {!isHackerNews && news.tags && news.tags.length > 0 && (
                                <div className="flex items-center space-x-2 flex-wrap gap-2">
                                  {news.tags.map((tag, tidx) => (
                                    <span key={tidx} className="bg-[#0a0a0a] border border-[#00ff41]/30 text-[#00ff41] px-2 lg:px-3 py-1 rounded-full text-xs">
                                      #{tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center space-x-4 lg:space-x-6 text-sm text-[#666]">
                            {isHackerNews ? (
                              <>
                                <div className="flex items-center space-x-2">
                                  <i className="ri-arrow-up-line"></i>
                                  <span>{news.score}</span>
                                </div>
                                {news.descendants !== undefined && (
                                  <div className="flex items-center space-x-2">
                                    <i className="ri-chat-3-line"></i>
                                    <span>{news.descendants}</span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <div className="flex items-center space-x-2">
                                  <i className="ri-chat-3-line"></i>
                                  <span>{news.replies_count}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <i className="ri-heart-line"></i>
                                  <span>{news.likes_count}</span>
                                </div>
                              </>
                            )}
                            <div className="flex items-center space-x-2 hover:text-[#00ff41] transition-colors">
                              <i className="ri-external-link-line"></i>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Bug Bounty Zone Tab */}
            {activeTab === 'bugbounty' && (
              <div>
                <div className="mb-4 lg:mb-6">
                  <h1 className="text-2xl lg:text-3xl font-black mb-2">{t('bugbounty.title')}</h1>
                  <p className="text-[#666] text-sm">{t('bugbounty.subtitle')}</p>
                </div>

                {/* Bug Bounty Platforms */}
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                    <i className="ri-shield-star-line text-[#00ff41] mr-2"></i>
                    {t('bugbounty.platforms.title')}
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
                    {[
                      {
                        name: 'HackerOne',
                        maxBounty: '$1,000,000+',
                        scope: 'Web, Mobile, API, Cloud',
                        difficulty: 'All Levels',
                        programs: '3,000+',
                        url: 'hackerone.com',
                        color: 'from-purple-500/20 to-pink-500/20 border-purple-500'
                      },
                      {
                        name: 'Bugcrowd',
                        maxBounty: '$500,000+',
                        scope: 'Web, Mobile, Hardware',
                        difficulty: 'Intermediate',
                        programs: '1,500+',
                        url: 'bugcrowd.com',
                        color: 'from-orange-500/20 to-red-500/20 border-orange-500'
                      },
                      {
                        name: 'Intigriti',
                        maxBounty: '€250,000+',
                        scope: 'Web, API, Blockchain',
                        difficulty: 'Advanced',
                        programs: '800+',
                        url: 'intigriti.com',
                        color: 'from-blue-500/20 to-cyan-500/20 border-blue-500'
                      },
                      {
                        name: 'Samsung MPS',
                        maxBounty: '$200,000',
                        scope: 'Galaxy, Knox, Tizen',
                        difficulty: 'Expert',
                        programs: '1',
                        url: 'security.samsungmobile.com',
                        color: 'from-blue-600/20 to-blue-400/20 border-blue-600'
                      },
                      {
                        name: 'Kakao Bug Bounty',
                        maxBounty: '₩50,000,000',
                        scope: 'KakaoTalk, Services',
                        difficulty: 'Advanced',
                        programs: '1',
                        url: 'kakaocorp.com/page/security',
                        color: 'from-yellow-500/20 to-yellow-300/20 border-yellow-500'
                      },
                      {
                        name: 'Naver Bug Bounty',
                        maxBounty: '₩30,000,000',
                        scope: 'Naver, Line, Clova',
                        difficulty: 'Advanced',
                        programs: '1',
                        url: 'bugbounty.naver.com',
                        color: 'from-green-500/20 to-emerald-500/20 border-green-500'
                      },
                      {
                        name: 'Google VRP',
                        maxBounty: '$150,000+',
                        scope: 'Chrome, Android, Cloud',
                        difficulty: 'Expert',
                        programs: '50+',
                        url: 'bughunters.google.com',
                        color: 'from-red-500/20 to-yellow-500/20 border-red-500'
                      },
                      {
                        name: 'Microsoft MSRC',
                        maxBounty: '$250,000',
                        scope: 'Windows, Azure, Office',
                        difficulty: 'Expert',
                        programs: '30+',
                        url: 'msrc.microsoft.com',
                        color: 'from-blue-500/20 to-sky-500/20 border-blue-500'
                      }
                    ].map((platform, idx) => (
                      <div
                        key={idx}
                        className={`bg-gradient-to-br ${platform.color} border rounded-lg p-4 lg:p-6 hover:border-[#00ff41] transition-all cursor-pointer`}
                        onClick={() => window.open(`https://${platform.url}`, '_blank')}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-white text-lg lg:text-xl font-bold mb-1">{platform.name}</h3>
                            <p className="text-[#00ff41] text-sm font-['Space_Mono']">{platform.programs} Programs</p>
                          </div>
                          <div className="text-right">
                            <div className="text-[#00ff41] text-base lg:text-xl font-bold mb-1 leading-tight">{platform.maxBounty}</div>
                            <div className="text-[#666] text-xs">Max Bounty</div>
                          </div>
                        </div>

                        <div className="space-y-2 mb-4">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#888]">Scope</span>
                            <span className="text-white">{platform.scope}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#888]">Difficulty</span>
                            <span className="text-white">{platform.difficulty}</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-[#666] text-xs font-['Space_Mono'] truncate mr-2">{platform.url}</span>
                          <button className="w-8 h-8 flex items-center justify-center bg-[#00ff41] text-black rounded-full hover:shadow-[0_0_15px_rgba(0,255,65,0.5)] transition-all cursor-pointer flex-shrink-0">
                            <i className="ri-external-link-line"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Writeups */}
                <div className="mb-8">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                    <i className="ri-file-text-line text-[#00ff41] mr-2"></i>
                    {t('bugbounty.writeups.title')}
                  </h2>
                  <div className="space-y-3 lg:space-y-4">
                    {[
                      {
                        title: 'RCE in Google Cloud Shell via Electron IPC Exploitation',
                        author: 'h4x0r_dz',
                        bounty: '$133,700',
                        platform: 'Google VRP',
                        severity: 'Critical',
                        date: '2024-12-15',
                        tags: ['rce', 'electron', 'gcp']
                      },
                      {
                        title: 'Samsung Knox Container Escape via Binder UAF',
                        author: 'pwn_master',
                        bounty: '$200,000',
                        platform: 'Samsung MPS',
                        severity: 'Critical',
                        date: '2024-12-10',
                        tags: ['android', 'kernel', 'uaf']
                      },
                      {
                        title: 'Microsoft Azure AD Authentication Bypass',
                        author: 'sec_researcher',
                        bounty: '$100,000',
                        platform: 'Microsoft MSRC',
                        severity: 'High',
                        date: '2024-12-05',
                        tags: ['azure', 'auth-bypass', 'cloud']
                      },
                      {
                        title: 'HackerOne SSRF to Internal AWS Metadata Access',
                        author: 'bug_hunter',
                        bounty: '$25,000',
                        platform: 'HackerOne',
                        severity: 'High',
                        date: '2024-11-28',
                        tags: ['ssrf', 'aws', 'metadata']
                      },
                      {
                        title: 'Kakao Account Takeover via OAuth Misconfiguration',
                        author: 'korean_hacker',
                        bounty: '₩30,000,000',
                        platform: 'Kakao',
                        severity: 'Critical',
                        date: '2024-11-20',
                        tags: ['oauth', 'account-takeover', 'web']
                      },
                      {
                        title: 'Naver Line E2EE Bypass via Man-in-the-Middle',
                        author: 'crypto_ninja',
                        bounty: '₩25,000,000',
                        platform: 'Naver',
                        severity: 'High',
                        date: '2024-11-15',
                        tags: ['e2ee', 'mitm', 'crypto']
                      },
                      {
                        title: 'Bugcrowd Platform XSS to Admin Panel Access',
                        author: 'xss_king',
                        bounty: '$20,000',
                        platform: 'Bugcrowd',
                        severity: 'High',
                        date: '2024-11-10',
                        tags: ['xss', 'privilege-escalation', 'web']
                      },
                      {
                        title: 'Intigriti Smart Contract Reentrancy Attack',
                        author: 'blockchain_sec',
                        bounty: '€50,000',
                        platform: 'Intigriti',
                        severity: 'Critical',
                        date: '2024-11-05',
                        tags: ['blockchain', 'smart-contract', 'reentrancy']
                      }
                    ].map((writeup, idx) => {
                      const getSeverityColor = (severity: string) => {
                        if (severity === 'Critical') return 'bg-red-500/20 border-red-500 text-red-500';
                        if (severity === 'High') return 'bg-orange-500/20 border-orange-500 text-orange-500';
                        return 'bg-yellow-500/20 border-yellow-500 text-yellow-500';
                      };

                      return (
                        <div
                          key={idx}
                          className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 lg:p-6 hover:border-[#00ff41] transition-all cursor-pointer"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 pr-4">
                              <div className="flex items-center space-x-2 mb-2 flex-wrap gap-2">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getSeverityColor(writeup.severity)}`}>
                                  {writeup.severity.toUpperCase()}
                                </span>
                                <span className="text-[#00ff41] text-xs font-['Space_Mono']">{writeup.platform}</span>
                                <span className="text-[#666] text-xs">•</span>
                                <span className="text-[#666] text-xs">{writeup.date}</span>
                              </div>
                              <h3 className="text-white text-base lg:text-lg font-semibold mb-2">{writeup.title}</h3>
                              <div className="flex items-center space-x-3 text-sm text-[#888] mb-3">
                                <span className="font-['Space_Mono']">@{writeup.author}</span>
                                <span>•</span>
                                <span className="text-[#00ff41] font-bold">{writeup.bounty}</span>
                              </div>
                              <div className="flex items-center space-x-2 flex-wrap gap-2">
                                {writeup.tags.map((tag, tidx) => (
                                  <span key={tidx} className="bg-[#0a0a0a] border border-[#00ff41]/30 text-[#00ff41] px-2 lg:px-3 py-1 rounded-full text-xs">
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Bug Bounty Tips */}
                <div className="bg-gradient-to-br from-[#00ff41]/10 to-transparent border border-[#00ff41] rounded-2xl p-6 lg:p-8">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                    <i className="ri-lightbulb-line text-[#00ff41] mr-2"></i>
                    {t('bugbounty.tips.title')}
                  </h2>
                  <div className="space-y-4">
                    <div className="bg-[#0a0a0a]/50 border border-[#333] rounded-lg p-4">
                      <h3 className="text-white font-bold mb-2 flex items-center">
                        <i className="ri-number-1 text-[#00ff41] mr-2"></i>
                        {t('bugbounty.tips.start.title')}
                      </h3>
                      <p className="text-[#888] text-sm leading-relaxed">
                        {t('bugbounty.tips.start.description')}
                      </p>
                    </div>
                    <div className="bg-[#0a0a0a]/50 border border-[#333] rounded-lg p-4">
                      <h3 className="text-white font-bold mb-2 flex items-center">
                        <i className="ri-number-2 text-[#00ff41] mr-2"></i>
                        {t('bugbounty.tips.tools.title')}
                      </h3>
                      <p className="text-[#888] text-sm leading-relaxed">
                        {t('bugbounty.tips.tools.description')}
                      </p>
                    </div>
                    <div className="bg-[#0a0a0a]/50 border border-[#333] rounded-lg p-4">
                      <h3 className="text-white font-bold mb-2 flex items-center">
                        <i className="ri-number-3 text-[#00ff41] mr-2"></i>
                        {t('bugbounty.tips.report.title')}
                      </h3>
                      <p className="text-[#888] text-sm leading-relaxed">
                        {t('bugbounty.tips.report.description')}
                      </p>
                    </div>
                    <div className="bg-[#0a0a0a]/50 border border-[#333] rounded-lg p-4">
                      <h3 className="text-white font-bold mb-2 flex items-center">
                        <i className="ri-number-4 text-[#00ff41] mr-2"></i>
                        {t('bugbounty.tips.community.title')}
                      </h3>
                      <p className="text-[#888] text-sm leading-relaxed">
                        {t('bugbounty.tips.community.description')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Learning Sites Tab */}
            {activeTab === 'learning' && (
              <div>
                <div className="mb-4 lg:mb-6 mt-4 lg:mt-0">
                  <h1 className="text-2xl lg:text-3xl font-black mb-2">{t('learning.title')}</h1>
                  <p className="text-[#666] text-sm">{t('learning.subtitle')}</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
                  {learningSites.map((site, idx) => (
                    <div key={idx} className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4 lg:p-6 hover:border-[#00ff41] transition-all cursor-pointer">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-white text-lg lg:text-xl font-bold mb-1">{site.name}</h3>
                          <p className="text-[#00ff41] text-sm font-['Space_Mono']">{site.category}</p>
                        </div>
                        <div className="flex items-center space-x-1">
                          <i className="ri-star-fill text-[#00ff41] text-sm"></i>
                          <span className="text-white font-bold">{site.rating}</span>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[#666]">{t('learning.difficulty')}</span>
                          <span className="text-white">{site.difficulty}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[#666]">{t('learning.users')}</span>
                          <span className="text-white">{site.users}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-[#666] text-xs font-['Space_Mono'] truncate mr-2">{site.url}</span>
                        <button className="w-8 h-8 flex items-center justify-center bg-[#00ff41] text-black rounded-full hover:shadow-[0_0_15px_rgba(0,255,65,0.5)] transition-all cursor-pointer flex-shrink-0">
                          <i className="ri-external-link-line"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Premium Tab */}
            {activeTab === 'premium' && (
              <div>
                <div className="mb-4 lg:mb-6 mt-4 lg:mt-0">
                  <h1 className="text-2xl lg:text-3xl font-black mb-2">{t('premium.title')}</h1>
                  <p className="text-[#666] text-sm">{t('premium.subtitle')}</p>
                </div>

                <div className="space-y-3 lg:space-y-4">
                  {premiumArchives.map((archive, idx) => (
                    <div
                      key={idx}
                      className="relative bg-gradient-to-br from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-xl border border-[#00ff41]/30 rounded-2xl p-6 lg:p-8 overflow-hidden cursor-pointer hover:border-[#00ff41] transition-all"
                      onClick={() => setShowPremiumModal(true)}
                    >
                      {/* Glassmorphism Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>

                      {/* Lock Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                        <div className="text-center">
                          <div className="w-16 h-16 lg:w-20 lg:h-20 flex items-center justify-center mx-auto mb-4 bg-[#00ff41]/10 border-2 border-[#00ff41] rounded-full">
                            <i className="ri-lock-fill text-[#00ff41] text-3xl lg:text-4xl"></i>
                          </div>
                          <div className="text-white text-lg lg:text-xl font-bold mb-2">{t('premium.only')}</div>
                          <div className="text-[#00ff41] text-sm font-['Space_Mono']">{t('premium.exclusive')}</div>
                        </div>
                      </div>

                      {/* Content (Blurred) */}
                      <div className="relative blur-sm">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-white text-xl lg:text-2xl font-bold mb-3">{archive.title}</h3>
                            <div className="flex items-center space-x-3 lg:space-x-4 flex-wrap gap-2">
                              <span className="bg-[#00ff41]/20 border border-[#00ff41] text-[#00ff41] px-2 lg:px-3 py-1 rounded-full text-xs">
                                {archive.cases} {t('premium.cases')}
                              </span>
                              <span className="bg-red-500/20 border border-red-500 text-red-500 px-2 lg:px-3 py-1 rounded-full text-xs">
                                {archive.difficulty}
                              </span>
                            </div>
                          </div>
                        </div>

                        <p className="text-[#888] text-sm leading-relaxed">
                          Real hacking project trial and error detailed records premium archive.
                          Each case includes the entire process from problem discovery to final solution.
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 lg:mt-8 bg-gradient-to-br from-[#00ff41]/10 to-transparent border border-[#00ff41] rounded-2xl p-6 lg:p-8 text-center">
                  <i className="ri-vip-crown-fill text-[#00ff41] text-4xl lg:text-5xl mb-4"></i>
                  <h3 className="text-white text-xl lg:text-2xl font-bold mb-3">{t('premium.membership')}</h3>
                  <p className="text-[#888] mb-6 max-w-md mx-auto text-sm lg:text-base">
                    {t('premium.description')}
                  </p>
                  <div className="text-[#00ff41] text-3xl lg:text-4xl font-black mb-6">{t('premium.price')}</div>
                  <button className="bg-[#00ff41] text-black px-8 lg:px-12 py-3 lg:py-4 rounded-full font-['Space_Mono'] font-bold text-base lg:text-lg hover:shadow-[0_0_30px_rgba(0,255,65,0.6)] transition-all cursor-pointer whitespace-nowrap">
                    {t('premium.upgrade.now')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Desktop Right Sidebar */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24 space-y-6">
              {/* Live Stats */}
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <div className="font-['Space_Mono'] text-[#00ff41] text-xs mb-4">{t('stats.title')}</div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[#888] text-sm">{t('stats.online.hackers')}</span>
                    <span className="text-white font-bold">2,847</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#888] text-sm">{t('stats.today.posts')}</span>
                    <span className="text-white font-bold">{feedPosts.length + studyPosts.length + knowledgePosts.length}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#888] text-sm">{t('stats.active.threads')}</span>
                    <span className="text-white font-bold">{jobPosts.length}</span>
                  </div>
                </div>
              </div>

              {/* Trending Tags */}
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <div className="font-['Space_Mono'] text-[#00ff41] text-xs mb-4">{t('stats.trending.tags')}</div>
                <div className="flex flex-wrap gap-2">
                  {['zero-day', 'oscp', 'bug-bounty', 'ctf', 'malware', 'web-security', 'reverse-eng', 'crypto'].map((tag, idx) => (
                    <button
                      key={idx}
                      className="bg-[#0a0a0a] border border-[#00ff41]/30 text-[#00ff41] px-4 py-1 rounded-full text-xs hover:bg-[#00ff41] hover:text-black transition-all cursor-pointer whitespace-nowrap"
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Top Contributors */}
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <div className="font-['Space_Mono'] text-[#00ff41] text-xs mb-4">{t('stats.top.hackers')}</div>
                <div className="space-y-3">
                  {[
                    { name: 'ghost_0x7f', points: 15420, rank: 1 },
                    { name: 'cipher_master', points: 12890, rank: 2 },
                    { name: 'exploit_dev', points: 11234, rank: 3 },
                  ].map((user) => (
                    <div key={user.rank} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                          user.rank === 1 ? 'bg-[#00ff41] text-black' : 'bg-[#333] text-[#666]'
                        }`}>
                          {user.rank}
                        </div>
                        <span className="text-white text-sm font-['Space_Mono']">{user.name}</span>
                      </div>
                      <span className="text-[#00ff41] text-xs">{user.points}p</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0a0a0a] border-t border-[#00ff41]/20 lg:hidden z-40">
        <div className="grid grid-cols-6 gap-1 px-2 py-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`flex flex-col items-center justify-center py-2 rounded-lg transition-all cursor-pointer ${
                activeTab === item.id ? 'bg-[#00ff41] text-black' : 'text-[#666]'
              }`}
            >
              <i className={`${item.icon} text-lg mb-1`}></i>
              <span className="text-[10px] font-['Space_Mono'] whitespace-nowrap">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Write Post Modal */}
      {showWriteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowWriteModal(false)}>
          <div className="bg-[#1a1a1a] border border-[#00ff41] rounded-2xl p-6 lg:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-xl lg:text-2xl font-bold">
                {writeModalType === 'feed' && t('feed.new.post')}
                {writeModalType === 'job' && t('jobs.post.job')}
                {writeModalType === 'study' && t('study.ask.question')}
                {writeModalType === 'knowledge' && t('knowledge.ask.bounty')}
                {writeModalType === 'news' && t('news.share.news')}
              </h3>
              <button
                onClick={() => setShowWriteModal(false)}
                className="w-8 h-8 flex items-center justify-center text-[#666] hover:text-[#00ff41] transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmitPost} className="space-y-4">
              {/* Feed Post Form */}
              {writeModalType === 'feed' && (
                <>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">카테고리</label>
                    <select name="category" className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none">
                      <option value="vulnerability">Vulnerability</option>
                      <option value="malware">Malware</option>
                      <option value="tools">Tools</option>
                      <option value="ctf">CTF</option>
                      <option value="career">Career</option>
                      <option value="bugbounty">Bug Bounty</option>
                      <option value="redteam">Red Team</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">제목</label>
                    <input
                      type="text"
                      name="title"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none"
                      placeholder="제목을 입력하세요..."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">내용</label>
                    <textarea
                      name="content"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none min-h-[200px]"
                      placeholder="내용을 입력하세요..."
                      required
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">태그 (쉼표로 구분)</label>
                    <input
                      type="text"
                      name="tags"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none"
                      placeholder="zero-day, critical, security"
                    />
                  </div>
                </>
              )}

              {/* Job Post Form */}
              {writeModalType === 'job' && (
                <>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">회사명 *</label>
                    <input
                      type="text"
                      name="company"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none"
                      placeholder="회사명을 입력하세요..."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">포지션 *</label>
                    <input
                      type="text"
                      name="position"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none"
                      placeholder="예: Senior Pentester"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#888] text-sm mb-2">카테고리 *</label>
                      <select
                        name="category"
                        className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none cursor-pointer"
                        required
                      >
                        <option value="">선택하세요</option>
                        <option value="pentest">Penetration Testing</option>
                        <option value="redteam">Red Team</option>
                        <option value="appsec">Application Security</option>
                        <option value="cloud">Cloud Security</option>
                        <option value="soc">SOC / Incident Response</option>
                        <option value="research">Security Research</option>
                        <option value="bugbounty">Bug Bounty</option>
                        <option value="consulting">Consulting / GRC</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[#888] text-sm mb-2">마감일 *</label>
                      <input
                        type="date"
                        name="deadline"
                        className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none cursor-pointer"
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#888] text-sm mb-2">지역 *</label>
                      <div className="flex gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="region"
                            value="domestic"
                            className="w-4 h-4 accent-[#00ff41] cursor-pointer"
                            defaultChecked
                            required
                          />
                          <span className="text-white text-sm">국내</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="region"
                            value="overseas"
                            className="w-4 h-4 accent-[#00ff41] cursor-pointer"
                            required
                          />
                          <span className="text-white text-sm">해외</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[#888] text-sm mb-2">경력 *</label>
                      <div className="flex gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="level"
                            value="junior"
                            className="w-4 h-4 accent-[#00ff41] cursor-pointer"
                            defaultChecked
                            required
                          />
                          <span className="text-white text-sm">신입</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="level"
                            value="senior"
                            className="w-4 h-4 accent-[#00ff41] cursor-pointer"
                            required
                          />
                          <span className="text-white text-sm">경력</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#888] text-sm mb-2">연봉 *</label>
                      <input
                        type="text"
                        name="salary"
                        className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none"
                        placeholder="예: $120,000 - $150,000"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[#888] text-sm mb-2">위치 *</label>
                      <input
                        type="text"
                        name="location"
                        className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none"
                        placeholder="예: 서울/원격"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#888] text-sm mb-2">경력 요구사항 *</label>
                    <input
                      type="text"
                      name="experience"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none"
                      placeholder="예: 5년 이상"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[#888] text-sm mb-2">공고 소개 *</label>
                    <textarea
                      name="description"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none min-h-[100px]"
                      placeholder="채용 공고에 대한 간단한 소개를 작성하세요..."
                      required
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-[#888] text-sm mb-2">주요 업무</label>
                    <textarea
                      name="responsibilities"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none min-h-[120px]"
                      placeholder="각 업무를 줄바꿈으로 구분하세요&#10;예:&#10;웹 애플리케이션 침투 테스트 수행&#10;보안 취약점 분석 및 리포트 작성&#10;고객사 보안 컨설팅"
                    ></textarea>
                    <p className="text-[#666] text-xs mt-1">각 항목을 줄바꿈으로 구분하세요</p>
                  </div>

                  <div>
                    <label className="block text-[#888] text-sm mb-2">자격 요건</label>
                    <textarea
                      name="requirements"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none min-h-[120px]"
                      placeholder="각 요건을 줄바꿈으로 구분하세요&#10;예:&#10;정보보안 관련 학위 또는 동등한 경력&#10;OSCP, CEH 등 보안 자격증 보유&#10;Burp Suite, Metasploit 등 도구 사용 경험"
                    ></textarea>
                    <p className="text-[#666] text-xs mt-1">각 항목을 줄바꿈으로 구분하세요</p>
                  </div>

                  <div>
                    <label className="block text-[#888] text-sm mb-2">우대 사항</label>
                    <textarea
                      name="preferred"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none min-h-[100px]"
                      placeholder="각 우대사항을 줄바꿈으로 구분하세요&#10;예:&#10;Bug Bounty 프로그램 참여 경험&#10;오픈소스 보안 프로젝트 기여 경험&#10;영어 능통자"
                    ></textarea>
                    <p className="text-[#666] text-xs mt-1">각 항목을 줄바꿈으로 구분하세요</p>
                  </div>

                  <div>
                    <label className="block text-[#888] text-sm mb-2">외부 지원 URL *</label>
                    <input
                      type="url"
                      name="apply_url"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none"
                      placeholder="https://company.com/careers/apply"
                      required
                    />
                    <p className="text-[#666] text-xs mt-1">지원자가 바로 이동할 수 있는 정확한 채용 공고 URL을 입력해주세요</p>
                  </div>

                  <div>
                    <label className="block text-[#888] text-sm mb-2">태그 (쉼표로 구분)</label>
                    <input
                      type="text"
                      name="tags"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none"
                      placeholder="pentest, burp-suite, oscp, remote"
                    />
                  </div>
                </>
              )}

              {/* Knowledge Bounty Form */}
              {writeModalType === 'knowledge' && (
                <>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">질문</label>
                    <input
                      type="text"
                      name="title"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none"
                      placeholder="질문을 입력하세요..."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">상세 설명</label>
                    <textarea
                      name="content"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none min-h-[200px]"
                      placeholder="문제에 대한 자세한 정보를 제공하세요..."
                      required
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">현상금 포인트</label>
                    <input
                      type="number"
                      name="bounty"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none"
                      placeholder="500"
                      min="100"
                      step="100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">태그 (쉼표로 구분)</label>
                    <input
                      type="text"
                      name="tags"
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-4 py-3 text-white focus:border-[#00ff41] focus:outline-none"
                      placeholder="aws, security, cloud"
                    />
                  </div>
                </>
              )}

              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-[#00ff41] text-black py-3 rounded-lg font-['Space_Mono'] font-bold hover:shadow-[0_0_20px_rgba(0,255,65,0.5)] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? '등록중...' : '등록하기'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowWriteModal(false)}
                  className="flex-1 border border-[#333] text-[#666] py-3 rounded-lg font-['Space_Mono'] hover:border-[#00ff41] hover:text-[#00ff41] transition-all cursor-pointer whitespace-nowrap"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Premium Modal */}
      {showPremiumModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 lg:p-6" onClick={() => setShowPremiumModal(false)}>
          <div className="bg-[#1a1a1a] border border-[#00ff41] rounded-2xl p-6 lg:p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 lg:w-20 lg:h-20 flex items-center justify-center mx-auto mb-4 lg:mb-6 bg-[#00ff41]/10 border-2 border-[#00ff41] rounded-full">
                <i className="ri-vip-crown-fill text-[#00ff41] text-3xl lg:text-4xl"></i>
              </div>
              <h3 className="text-white text-xl lg:text-2xl font-bold mb-3 lg:mb-4">{t('premium.modal.title')}</h3>
              <p className="text-[#888] mb-4 lg:mb-6 text-sm lg:text-base">
                {t('premium.modal.description')}
              </p>
              <div className="text-[#00ff41] text-2xl lg:text-3xl font-black mb-4 lg:mb-6">{t('premium.price')}</div>
              <button className="w-full bg-[#00ff41] text-black py-3 lg:py-4 rounded-full font-['Space_Mono'] font-bold hover:shadow-[0_0_30px_rgba(0,255,65,0.6)] transition-all mb-3 cursor-pointer whitespace-nowrap">
                {t('premium.modal.upgrade')}
              </button>
              <button
                onClick={() => setShowPremiumModal(false)}
                className="w-full border border-[#333] text-[#666] py-2.5 lg:py-3 rounded-full font-['Space_Mono'] hover:border-[#00ff41] hover:text-[#00ff41] transition-all cursor-pointer whitespace-nowrap"
              >
                {t('premium.modal.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Space+Mono:wght@400;700&display=swap');

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
